(() => {
  const formatter = new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2
  });

  const pieceBasedHandles = new Set([
    'grillworst-naturel-huisgemaakt-500-gram',
    'grillworst-kaas-huisgemaakt-500-gram-ongegrild',
    'grillworst-jalapeno-cheddar-500-gram-ongegrild',
    'kipgrillworst-jalapeno-cheddar-huisgemaakt-500-gram-ongegrild'
  ]);
  const fixedKilograms = {
    'spareribs-10-kg': 10,
    'platte-bil-met-zwoerd': 3.25,
    'spierstuk-dikke-lende-met-zwoerd': 4,
    'varkensschouder-zonder-been-en-zwoerd': 5,
    'procureur-heel-2-5-kg': 2.5
  };

  const priceRequests = new Map();
  const resolvedPrices = new Map();

  const requestMarkup = async (url) => {
    let lastError;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const requestUrl = new URL(url, window.location.origin);
        requestUrl.searchParams.set('mfm_bundle_price', String(Math.floor(Date.now() / 300000)));
        const response = await fetch(requestUrl, {
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { Accept: 'text/html' }
        });
        if (!response.ok) throw new Error(String(response.status));
        return await response.text();
      } catch (error) {
        lastError = error;
        if (attempt === 0) await new Promise((resolve) => setTimeout(resolve, 350));
      }
    }
    throw lastError;
  };

  const totalPrice = (bar, basePrice) => {
    const quantity = Number(bar.quantity) || 1;
    const value = Number(bar.discountValue);
    if (bar.discountType === 'specific' && Number.isFinite(value)) return value;
    if (bar.discountType === 'percentage' && Number.isFinite(value)) return basePrice * quantity * (1 - value / 100);
    if (bar.discountType === 'fixed' && Number.isFinite(value)) return Math.max(0, basePrice * quantity - value);
    return basePrice * quantity;
  };

  const units = (bar, handle) => {
    const title = String(bar.title || '').toLowerCase().replace(/,/g, '.');
    if (pieceBasedHandles.has(handle)) return { amount: Number(bar.quantity) || 1, label: 'stuk' };
    const quantity = Number(bar.quantity) || 1;
    const multipliedKilograms = title.match(/(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)\s*(?:kg|kilo)\b/);
    if (multipliedKilograms) return { amount: Number(multipliedKilograms[1]) * Number(multipliedKilograms[2]), label: 'kilo' };
    const kilograms = title.match(/(\d+(?:\.\d+)?)\s*(?:kg|kilo)\b/);
    if (kilograms) return { amount: Number(kilograms[1]), label: 'kilo' };
    if (fixedKilograms[handle]) return { amount: quantity * fixedKilograms[handle], label: 'kilo' };
    return { amount: quantity, label: 'stuk' };
  };

  const showPrice = (url, text) => {
    resolvedPrices.set(url, text);
    document.querySelectorAll(`[data-mfm-bundle-price][data-product-url="${CSS.escape(url)}"]`).forEach((card) => {
      if (card.textContent !== text) card.textContent = text;
      card.style.visibility = '';
      card.removeAttribute('aria-busy');
    });
  };

  const getCandidates = async (url, handle, basePrice) => {
    const markup = await requestMarkup(url);
    const documentCopy = new DOMParser().parseFromString(markup, 'text/html');
    const blocks = Array.from(documentCopy.querySelectorAll('script.kaching-bundles-deal-block-settings[type="application/json"]'));
    const candidates = [];

    blocks.forEach((block) => {
      try {
        const deal = JSON.parse(block.textContent);
        (deal.dealBars || []).forEach((bar) => {
          const unit = units(bar, handle);
          const total = totalPrice(bar, basePrice);
          if (unit.amount > 0 && total > 0) candidates.push({ price: total / unit.amount, label: unit.label });
        });
      } catch (error) {
        return;
      }
    });

    return candidates;
  };

  const loadPrice = async (element) => {
    const url = element.dataset.productUrl;
    const handle = element.dataset.mfmProductHandle;
    const basePrice = Number(element.dataset.basePrice) / 100;
    if (!url || !handle || !basePrice) return;

    try {
      if (!priceRequests.has(url)) priceRequests.set(url, getCandidates(url, handle, basePrice));
      const candidates = await priceRequests.get(url);
      if (!candidates.length) {
        showPrice(url, 'Bekijk actuele staffelprijs');
        return;
      }
      candidates.sort((left, right) => left.price - right.price);
      const lowest = candidates[0];
      showPrice(url, `Vanaf slechts ${formatter.format(lowest.price)} per ${lowest.label}`);
    } catch (error) {
      priceRequests.delete(url);
      showPrice(url, 'Bekijk actuele staffelprijs');
    }
  };

  const loadNewCards = () => {
    document.querySelectorAll('[data-mfm-bundle-price]:not([data-mfm-loading])').forEach((element) => {
      const resolvedPrice = resolvedPrices.get(element.dataset.productUrl);
      if (resolvedPrice) {
        element.dataset.mfmLoading = 'true';
        showPrice(element.dataset.productUrl, resolvedPrice);
        return;
      }
      element.dataset.mfmLoading = 'true';
      loadPrice(element);
    });
  };

  const restoreResolvedPrices = () => {
    resolvedPrices.forEach((text, url) => showPrice(url, text));
  };

  const reloadPrices = () => {
    priceRequests.clear();
    resolvedPrices.clear();
    document.querySelectorAll('[data-mfm-bundle-price]').forEach((element) => {
      element.style.visibility = 'hidden';
      element.setAttribute('aria-busy', 'true');
      element.removeAttribute('data-mfm-loading');
    });
    loadNewCards();
  };

  let refreshQueued = false;
  new MutationObserver(() => {
    if (refreshQueued) return;
    refreshQueued = true;
    window.requestAnimationFrame(() => {
      refreshQueued = false;
      loadNewCards();
      restoreResolvedPrices();
    });
  }).observe(document.documentElement, { childList: true, characterData: true, subtree: true });
  window.addEventListener('pageshow', reloadPrices);
  document.addEventListener('shopify:section:load', reloadPrices);
  loadNewCards();
})();
